import { useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { BulkCreateRestaurantEntryPayload } from "src/data/admin/types/restaurant";
import { BulkCreateResult } from "src/data/admin/types/restaurant";
import { useBulkCreateRestaurants } from "src/data/admin/queries/restaurants";
import BulkRestaurantDropzone from "./BulkRestaurantDropzone";
import BulkRestaurantTable from "./BulkRestaurantTable";
import BulkRestaurantResultBubble from "./BulkRestaurantResultBubble";
import { parseCsv } from "./csvParser";

const BulkCreateRestaurants = () => {
    const [entries, setEntries] = useState<BulkCreateRestaurantEntryPayload[] | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const [result, setResult] = useState<BulkCreateResult | null>(null);

    console.log('result: ', result)
    console.log('parseError: ', parseError)

    const { mutate, isPending } = useBulkCreateRestaurants();

    const handleFile = (file: File) => {
        setParseError(null);
        setEntries(null);
        setResult(null);

        if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
            setParseError("Only CSV files are accepted.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const parsed = parseCsv(e.target?.result as string);
            if (parsed.ok) {
                setEntries(parsed.entries);
            } else {
                setParseError(parsed.error);
            }
        };
        reader.readAsText(file);
    };

    const handleClear = () => {
        setEntries(null);
        setParseError(null);
        setResult(null);
    };

    const handleSubmit = () => {
        if (!entries) return;
        mutate(entries, {
            onSuccess: (data) => setResult(data),
            onError: (err) => setParseError(err.message),
        });
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <RestaurantMenuIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="h6">Bulk Create Restaurants</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Upload a CSV file to create multiple restaurants at once. One row per restaurant.
            </Typography>

            <BulkRestaurantDropzone entries={entries} onFile={handleFile} />

            {parseError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {parseError}
                </Alert>
            )}

            {result && (
                <BulkRestaurantResultBubble
                    result={result}
                    onDismiss={() => setResult(null)}
                />
            )}

            {entries && entries.length > 0 && (
                <BulkRestaurantTable entries={entries} />
            )}

            <Box mt={3} display="flex" alignItems="center" gap={2}>
                <CustomLoadingButton
                    loading={isPending}
                    disabled={!entries || entries.length === 0}
                    onSubmit={handleSubmit}
                    label={isPending ? "Creating…" : `Create ${entries?.length ?? 0} Restaurant${(entries?.length ?? 0) !== 1 ? "s" : ""}`}
                    color="success"
                    fullwidth={false}
                />
                {entries && (
                    <CustomButton color="inverted" onClick={handleClear}>
                        Clear
                    </CustomButton>
                )}
            </Box>
        </Box>
    );
};

export default BulkCreateRestaurants;
